import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useFeatureFlags from '../hooks/useFeatureFlags';
import { reopenBanner } from '../lib/consentGate';
import HotmailDeliveryNotice from './HotmailDeliveryNotice';
import './Footer.css';

function FooterNewsletter() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const r = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'footer' }),
      });
      if (r.ok) setDone(true);
      else { const j = await r.json().catch(() => ({})); setErr(j.error || 'Signup failed'); }
    } catch (_) { setErr('Network error'); }
    finally { setBusy(false); }
  };

  return (
    <div className="footer-section footer-newsletter">
      <h4 style={{ marginTop: 0 }}>Stay in the loop</h4>
      <p style={{ fontSize: 13, opacity: 0.85 }}>
        New drops, restock alerts, and exclusive deals. One email a week, no spam.
      </p>
      {done ? (
        <p style={{ fontSize: 13, color: '#43A047' }}>✓ Thanks — check your inbox to confirm.</p>
      ) : (
        <form onSubmit={submit} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <input
            type="email" required
            placeholder="your@email.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            style={{ flex: '1 1 180px', minWidth: 0, padding: '8px 10px', borderRadius: 4, border: 'none', fontSize: 16 }}
          />
          <button type="submit" className="btn btn-primary" disabled={busy} style={{ padding: '8px 14px', fontSize: 13 }}>
            {busy ? '…' : 'Subscribe'}
          </button>
          <div style={{ flexBasis: '100%' }}>
            <HotmailDeliveryNotice email={email} />
            {err && <small style={{ color: '#ff6b6b' }}>{err}</small>}
          </div>
        </form>
      )}
      <p style={{ fontSize: 11, opacity: 0.7, marginTop: 8 }}>
        By subscribing you agree to our <Link to="/privacy" style={{ color: 'inherit' }}>privacy policy</Link>. Unsubscribe anytime.
      </p>
    </div>
  );
}

const Footer = () => {
  const flags = useFeatureFlags();
  // On phone (≤640px) default the accordions to closed; on desktop default
  // them open. <details open> is set in JSX so SSR/no-JS users see the
  // expanded state; this effect strips [open] on phones at mount.
  const [defaultOpen, setDefaultOpen] = useState(
    typeof window === 'undefined' ? true : window.matchMedia('(min-width: 641px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 641px)');
    const onChange = () => setDefaultOpen(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  const sellEnabled = flags['sell.enabled'] !== false && flags['sell.enabled'] !== 0;
  // Pull the public site_settings once for the footer business address. CASL
  // (and good practice) wants a real mailing address visible on every page;
  // editable from Admin → Site Settings without code changes.
  const [settings, setSettings] = useState({});
  useEffect(() => {
    fetch('/api/site/settings/public')
      .then(r => r.ok ? r.json() : {})
      .then(setSettings)
      .catch(() => {});
  }, []);

  const addressLines = [
    settings['business.address'],
    [settings['business.city'], settings['business.province']].filter(Boolean).join(', '),
    settings['business.postal_code'],
  ].filter(Boolean);

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-section">
            <h3>SilverGames</h3>
            <p style={{ fontSize: 13, opacity: 0.9, marginBottom: 6 }}>
              A Division of The Silver Fern Company Inc.
            </p>
            <p>Canadian-owned Pokemon card shop. Singles, sealed, graded slabs.</p>
            {addressLines.length > 0 && (
              <address style={{ marginTop: 10, fontSize: 12, opacity: 0.85, fontStyle: 'normal', lineHeight: 1.5 }}>
                {addressLines.map((line, i) => <div key={i}>{line}</div>)}
              </address>
            )}
            {!addressLines.length && (
              <p style={{ marginTop: 8, fontSize: 13, opacity: 0.8 }}>
                Ships from {settings['business.ships_from'] || 'Ontario, Canada'}
              </p>
            )}
            {!addressLines.length && typeof window !== 'undefined' && localStorage.getItem('admin_token') && (
              <p style={{ marginTop: 8, fontSize: 12, color: '#ffde00', background: 'rgba(0,0,0,0.3)', padding: '4px 8px', borderRadius: 4, display: 'inline-block' }}>
                ⚠ Admin: set your business address in <a href="/admin/site-settings" style={{ color: '#ffde00', textDecoration: 'underline' }}>Site Settings</a> for CASL compliance.
              </p>
            )}
          </div>

          {/* Link sections render as standard 4-up grid on desktop and as
              expandable accordions on phone (industry pattern — Apple, Nike,
              Shopify all collapse footer link sections to keep the page short).
              Native <details>/<summary> handles open/close; CSS overrides
              force everything visible on desktop regardless of [open] state. */}
          <details className="footer-section footer-accordion" {...(defaultOpen ? { open: true } : {})}>
            <summary><h4>Shop</h4></summary>
            <ul>
              <li><Link to="/shop">All products</Link></li>
              <li><Link to="/shop/singles-ultra-rare">Ultra Rares</Link></li>
              <li><Link to="/shop/booster-packs">Booster Packs</Link></li>
              <li><Link to="/auctions">Live auctions</Link></li>
              <li><Link to="/pre-orders">Pre-orders</Link></li>
            </ul>
          </details>

          <details className="footer-section footer-accordion" {...(defaultOpen ? { open: true } : {})}>
            <summary><h4>Help</h4></summary>
            <ul>
              <li><Link to="/track">Track an order</Link></li>
              {sellEnabled && <li><Link to="/sell">Sell to us</Link></li>}
              <li><Link to="/blog">Blog</Link></li>
              <li><Link to="/events">Events</Link></li>
              <li><a href="mailto:support@silvergames.ca">support@silvergames.ca</a></li>
            </ul>
          </details>

          <details className="footer-section footer-accordion" {...(defaultOpen ? { open: true } : {})}>
            <summary><h4>Community</h4></summary>
            <a
              href="https://discord.gg/silvergames"
              target="_blank"
              rel="noopener noreferrer"
              className="discord-cta"
            >
              <div>
                <strong>Join our Discord</strong>
                <small>Drops · giveaways · trades</small>
              </div>
            </a>
            <div className="social-links" style={{ marginTop: 12 }}>
              <a href="https://instagram.com/silvergames" target="_blank" rel="noopener noreferrer">Instagram</a>
              <a href="https://tiktok.com/@silvergames"   target="_blank" rel="noopener noreferrer">TikTok</a>
              <a href="https://youtube.com/@silvergames"  target="_blank" rel="noopener noreferrer">YouTube</a>
              <a href="https://x.com/silvergames"          target="_blank" rel="noopener noreferrer">X</a>
            </div>
          </details>

          <FooterNewsletter />
        </div>

        <div className="footer-bottom">
          <p>
            © {new Date().getFullYear()} SilverGames · <Link to="/privacy">Privacy</Link> · <Link to="/terms">Terms</Link> ·{' '}
            <button
              type="button"
              onClick={reopenBanner}
              style={{ background: 'transparent', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}
            >Manage cookies</button>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
